import type { ImportStructure } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { cn } from '#client/lib/cn';
import { columnLetter } from './mapping.logic';

/**
 * The top of the sheet, as it looks in Excel.
 *
 * The single most useful thing on the mapping screen, and it is not a table of
 * parsed values — it is the raw grid, with Excel's own row numbers and column
 * letters. An operator deciding whether the header is on row 5 or row 8 is
 * looking at their own spreadsheet in their head, and anything that renumbers
 * or re-orders the cells makes that comparison impossible.
 *
 * The chosen header row is marked, and so are the block boundaries, so a wrong
 * guess about either is visible rather than inferred from a bad preview two
 * steps later.
 */
export function SheetPreview({ structure }: { structure: ImportStructure }) {
  const { run, sample } = structure;
  const width = Math.max(
    run.block_width,
    structure.sheet_headers.length,
    ...sample.map((row) => row.cells.length),
  );

  const inBlock = (column: number) =>
    run.block_offsets.some((offset) => column >= offset && column < offset + run.block_width);

  return (
    <Card
      title="The sheet, as it is"
      subtitle={`First ${sample.length} rows of ${run.sheet_name}`}
      flush
    >
      <div className="c-table__scroll" tabIndex={0}>
        <table className="c-sheet">
          <thead>
            <tr>
              <th scope="col" className="c-sheet__gutter" />
              {Array.from({ length: width }, (_, column) => (
                <th
                  key={column}
                  scope="col"
                  className={cn('c-sheet__letter', !inBlock(column) && 'c-sheet__cell--outside')}
                >
                  {columnLetter(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sample.map((row) => {
              const isHeader = row.row_no === run.header_row;
              const isAbove = row.row_no < run.header_row;

              return (
                <tr
                  key={row.row_no}
                  className={cn(
                    isHeader && 'c-sheet__row--header',
                    isAbove && 'c-sheet__row--above',
                  )}
                >
                  <th scope="row" className="c-sheet__gutter">
                    {row.row_no}
                  </th>
                  {Array.from({ length: width }, (_, column) => (
                    <td
                      key={column}
                      className={cn(
                        'c-sheet__cell',
                        !inBlock(column) && 'c-sheet__cell--outside',
                        run.block_offsets.includes(column) && 'c-sheet__cell--block-start',
                      )}
                    >
                      {row.cells[column]?.t ?? ''}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="c-field__help c-sheet__legend">
        Row {run.header_row} is being read as the column names. Rows above it are ignored, and so
        are columns outside the {run.block_offsets.length === 1 ? 'table' : 'tables'} — the shaded
        ones.
      </p>
    </Card>
  );
}
